import React from 'react';
import actions from '../../../../api/apiaApi/actions';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { arrayOrArray } from '../../../../utils/util';
import ActualTree from '../../../common/generic/tree/Tree';
import TreeDataController from '../../../common/generic/tree/TreeDataController';
import { TDataNode, TId, TTreeProps } from '../../../common/generic/tree/types';
import LabelRenderer from './LabelRenderer';
import LeafsAsLinkRenderer from './LeafsAsLinkRenderer';
import { TApiaCategory, TGetMenuItemsResponse } from './types';
import { Box } from '@apia/theme';
import { IOpenTabOptions } from '../tabsController';

export type TNodeProps = {
  className?: string;
  onClick?: (ev: React.KeyboardEvent | React.MouseEvent) => unknown;
  url?: string;
  tabTitle?: string;
  openTabOptions?: IOpenTabOptions;
};

async function getMenuItems(fncId: number) {
  return arrayOrArray(
    (
      await ApiaApi.post<TGetMenuItemsResponse>(
        makeApiaUrl({ action: actions.menu.filterMenu, fncId }),
      )
    )?.data?.category,
  );
}

function makeNode(
  current: TApiaCategory,
  parentId: TId,
  className?: string,
): TDataNode<TNodeProps> {
  return {
    children: [],
    id: current.id,
    label: current.LABEL,
    isLeaf: current.type === 'L',
    parentId,
    labelRenderer: current.type === 'L' ? LeafsAsLinkRenderer : LabelRenderer,
    nodeProps: {
      className,
      url: current.url.replaceAll('&amp;', '&'),
      tabTitle: current.LABEL,
      openTabOptions: {
        funcId: current.id,
      },
      onClick:
        current.type === 'L'
          ? (ev: React.KeyboardEvent | React.MouseEvent) =>
              window.tabsController.openTabByUrl(
                current.LABEL,
                current.url.replaceAll('&amp;', '&'),
                {
                  funcId: current.id,
                  keepMenuOpen: ev.ctrlKey,
                },
              )
          : undefined,
    },
  };
}

const Tree = ({
  onArrowUpOnFirstElement,
}: Pick<TTreeProps, 'onArrowUpOnFirstElement'>) => {
  const handler = React.useRef<TreeDataController<TNodeProps>>(
    {} as TreeDataController<TNodeProps>,
  );

  return (
    <Box
      onContextMenu={(ev) => {
        ev.preventDefault();
        const key = (ev.target as HTMLElement).closest<HTMLElement>(
          '[data-key]',
        )?.dataset.key as string;

        if (key) {
          const url =
            handler.current.propsStore.getFieldProps(key).nodeProps.url;

          if (url) {
            const handler = window.tabsController.getHandler();
            handler.focusTab(
              handler.tabsList.find((current) => {
                return current.state.additionalProps.originalUrl.match(
                  /desktop.jsp/,
                );
              })!.id,
            );
          }
        }
      }}
    >
      <ActualTree<TNodeProps>
        disableSelection
        focusGetter={React.useCallback((liElement: HTMLElement) => {
          return (liElement.querySelector(
            ':scope > .tree__nodeItemLabel .menu__openTask',
          ) ||
            liElement.querySelector(
              ':scope > .tree__nodeItemLabel .tree__mainLabel',
            )) as HTMLElement;
        }, [])}
        getHandler={React.useCallback(
          async (newHandler: TreeDataController<TNodeProps>) => {
            handler.current = newHandler;
            newHandler.batchInit();
            const menuItems = await getMenuItems(-1);
            menuItems.forEach((current) =>
              newHandler.append(makeNode(current, 'root', 'mainCategory')),
            );
            newHandler.batchFinish();
          },
          [],
        )}
        label={window.LBL_MAIN_MENU}
        name="MainMenu"
        onArrowUpOnFirstElement={onArrowUpOnFirstElement}
        onLoadData={React.useCallback(async (node: TDataNode<TNodeProps>) => {
          const menuItems = await getMenuItems(node.id as number);
          menuItems.forEach((current) =>
            handler.current.append(makeNode(current, node.id)),
          );
        }, [])}
        onNodeClick={React.useCallback(
          (
            ev: React.KeyboardEvent | React.MouseEvent,
            node: TDataNode<TNodeProps>,
          ) => {
            node.nodeProps?.onClick?.(ev);
          },
          [],
        )}
      />
    </Box>
  );
};

export default Tree;
